import { useState, useCallback, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../redux/actions/authActions.js";
import api from "../../services/api";
import { useNavbar } from "../../context/NavbarContext";
import NeurologicalResearchLoading from "../../components/NeurologicalResearchLoading";
import DesktopSidebar from "./DesktopSidebar";
import MobileNav from "./MobileNav";
import UserMenu from "./UserMenu";
import NotificationBell from "../../components/NotificationBell.jsx";
import SupportModal from "../../components/SupportModal";
import { ROUTES } from "../../config/constants.js";
import { clearCoinBounce } from "../../redux/actions/pointsActions.js";
import { MessageCircle } from 'lucide-react';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const points = useSelector((s) => s.points.value);
  const bounce = useSelector((s) => s.points.bounce);
  const { setShowNavbar, sidebarExpanded, setSidebarExpanded } = useNavbar();
  const [isRefreshingCredits, setIsRefreshingCredits] = useState(false);
  const [showNeurolawLoading, setShowNeurolawLoading] = useState(false);
  const [showPointsInfo, setShowPointsInfo] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);

  // Always initialize sidebar as collapsed (default to false)
  useEffect(() => {
    // Always start with sidebar collapsed
    setSidebarExpanded(false);
  }, [setSidebarExpanded]);

  const toggleSidebar = useCallback(() => {
    setSidebarExpanded(prev => !prev);
  }, [setSidebarExpanded]);

  const handleNavigation = useCallback(
    (path, isNeurolawResearch = false) => {
      if (path === ROUTES.USER_PROFILE_COMPLETION) {
        setShowNavbar(true);
      } else {
        setShowNavbar(true);
      }

      navigate(path);
    },
    [navigate, setShowNavbar]
  );

  const refreshUserCredits = useCallback(async () => {
    if (isRefreshingCredits) return;

    setIsRefreshingCredits(true);
    try {
      const response = await api.getUserBalance();
      console.log(response);
      if (response && response.credits !== undefined) {
        const updatedUser = { ...user, credits: response.credits };
        window.dispatchEvent(
          new CustomEvent("userDataUpdated", {
            detail: { user: updatedUser },
          })
        );
      }
    } catch (error) {
      console.error("Failed to refresh user credits:", error);
    } finally {
      setIsRefreshingCredits(false);
    }
  }, [isRefreshingCredits, user]);

  const handleLogout = useCallback(async () => {
    try {
      await dispatch(logoutUser());
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error("Logout failed:", error);
      navigate(ROUTES.LOGIN);
    }
  }, [dispatch, navigate]);

  const userActions = useMemo(() => ({
    logout: handleLogout,
    refreshCredits: refreshUserCredits,
    isRefreshing: isRefreshingCredits,
    Profile: () => handleNavigation(ROUTES.PROFILE),
    overview: () => handleNavigation(ROUTES.OVERVIEW),
    wallet: () => handleNavigation(ROUTES.USER_WALLET),
    help: () => console.log("Help clicked"),
    feedback: () => handleNavigation("/feedback"),
  }), [handleLogout, refreshUserCredits, isRefreshingCredits, handleNavigation]);

  // Fetch initial balance on mount
  useEffect(() => {
    const fetchInitialBalance = async () => {
      if (!isAuthenticated || !user) return;
      
      try {
        await api.getUserBalance();
      } catch (error) {
        console.error("Failed to fetch initial balance:", error);
      }
    };

    fetchInitialBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
      
      {/* Desktop Sidebar - Only visible on lg screens and above */}
      <DesktopSidebar
        isExpanded={sidebarExpanded}
        toggleSidebar={toggleSidebar}
        onNavigate={handleNavigation}
        user={user}
        userActions={userActions}
        onOpenSupport={() => setShowSupportModal(true)}
      />

      {/* Mobile Header - Only visible below lg screens */}
      <header
        className="fixed left-0 right-0 bg-white border-b border-slate-200 lg:hidden"
        style={{ zIndex: 1000, top: 0 }}
      >
        <div className="px-2 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-4">
            <Link
              to={ROUTES.HOME}
              className="flex items-center gap-2 group min-w-0 flex-shrink-0"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg text-white shadow-md group-hover:scale-105 transition-transform">
                <img
                  src="/logo.webp"
                  alt="Neurolaw AI Logo"
                  className="w-full h-full object-contain"
                  style={{ mixBlendMode: "multiply" }}
                />
              </div>
              <div className="min-w-0 hidden sm:block">
                <h1 className="text-sm sm:text-base font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent truncate">
                  Neurolaw AI
                </h1>
              </div>
            </Link>

            <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
              <button
                onClick={() => setShowSupportModal(true)}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                title="Support"
              >
                <MessageCircle size={20} />
              </button>
              <NotificationBell />
              <MobileNav
                user={user}
                actions={userActions}
                location={location}
                onNavigate={handleNavigation}
              />
            </div>
          </div>
        </div>
      </header>

      {showNeurolawLoading && <NeurologicalResearchLoading />}
    </>
  );
};

export default Navbar;
